import { ArrowLeft, CheckCircle, Printer, Save, Wifi } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../../../assets/scss/pages/SettingsPage.module.scss';
import api from '../../../config/axios-conf';

const DEFAULT_FORM = {
    ipAddress: '',
    port: '9100',
    paperWidth: '80',
    timeoutMs: '45000',
    printDensity: '3',
    trailingFeedLines: '3',
    cutAfterImagePrint: true,
    isActive: true,
};

function validate(form) {
    if (!form.ipAddress.trim()) return 'Vui lòng nhập địa chỉ IP máy in';
    const port = Number(form.port);
    if (!Number.isInteger(port) || port <= 0 || port > 65535) return 'Port không hợp lệ (1 – 65535)';
    const timeout = Number(form.timeoutMs);
    if (!Number.isFinite(timeout) || timeout < 1000 || timeout > 90000) return 'Timeout không hợp lệ (1000 – 90000 ms)';
    return null;
}

export default function PrinterSettings() {
    const navigate = useNavigate();
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(DEFAULT_FORM);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [testing, setTesting] = useState(false);
    const [error, setError] = useState('');
    const [toast, setToast] = useState(null);

    const showToast = useCallback((type, text) => {
        setToast({ type, text });
        setTimeout(() => setToast(null), 2500);
    }, []);

    const loadPrinter = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/printers');
            const printers = data?.data || [];
            if (printers.length === 0) {
                setEditingId(null);
                setForm(DEFAULT_FORM);
                return;
            }
            const selected = printers.find((p) => p.isDefault) || printers[0];
            setEditingId(selected.id);
            setForm({
                ipAddress: selected.ipAddress || '',
                port: String(selected.port || 9100),
                paperWidth: String(selected.paperWidth || 80),
                timeoutMs: String(selected.timeoutMs || 45000),
                printDensity: String(selected.printDensity || 3),
                trailingFeedLines: String(selected.trailingFeedLines || 3),
                cutAfterImagePrint: selected.cutAfterImagePrint !== false,
                isActive: selected.isActive !== false,
            });
        } catch {
            setError('Không thể tải cấu hình máy in');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadPrinter(); }, [loadPrinter]);

    const onChange = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        setError('');
    };

    const buildPayload = () => ({
        name: 'Máy in chính',
        connectionType: 'lan',
        ipAddress: form.ipAddress.trim(),
        port: Number(form.port),
        paperWidth: Number(form.paperWidth) === 80 ? 80 : 58,
        encoding: 'UTF8',
        timeoutMs: Number(form.timeoutMs) || 45000,
        retryCount: 1,
        printDensity: Math.min(5, Math.max(1, Number(form.printDensity) || 3)),
        trailingFeedLines: Math.min(5, Math.max(1, Number(form.trailingFeedLines) || 3)),
        cutAfterImagePrint: form.cutAfterImagePrint,
        isDefault: true,
        isActive: form.isActive,
    });

    const handleSave = async () => {
        if (submitting) return;
        const validationError = validate(form);
        if (validationError) { setError(validationError); return; }

        setSubmitting(true);
        setError('');
        try {
            if (editingId) {
                await api.patch(`/printers/${editingId}`, buildPayload());
            } else {
                const res = await api.post('/printers', buildPayload());
                const newId = res.data?.data?.id || res.data?.id;
                if (newId) {
                    await api.patch(`/printers/${newId}/default`).catch(() => {});
                }
            }
            showToast('success', 'Đã lưu cấu hình máy in');
            await loadPrinter();
        } catch (err) {
            const msg = err.response?.data?.message;
            setError(Array.isArray(msg) ? msg.join(', ') : msg || 'Lưu thất bại');
        } finally {
            setSubmitting(false);
        }
    };

    const handleTestPrint = async () => {
        if (testing) return;
        const validationError = validate(form);
        if (validationError) { setError(validationError); return; }

        setTesting(true);
        setError('');
        try {
            const id = editingId;
            if (!id) {
                setError('Vui lòng lưu cấu hình trước khi in thử');
                return;
            }
            await api.post(`/printers/${id}/test`, buildPayload());
            showToast('success', 'In thử thành công!');
        } catch (err) {
            const msg = err.response?.data?.message;
            setError(Array.isArray(msg) ? msg.join(', ') : msg || 'Không thể kết nối máy in');
        } finally {
            setTesting(false);
        }
    };

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <button className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ArrowLeft size={18} />
                </button>
                <div>
                    <h1 className={styles.title}>Máy in hoá đơn</h1>
                    <div className={styles.subtitle}>Cấu hình kết nối máy in nhiệt qua mạng LAN</div>
                </div>
            </div>

            {loading ? (
                <div style={{ padding: 48, textAlign: 'center', color: '#6B7280' }}>Đang tải...</div>
            ) : (
                <div className={styles.printerLayout}>
                    {/* LEFT: form */}
                    <div className={styles.printerLeft}>
                        {/* Connection */}
                        <div className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionTitle}>Kết nối</div>
                            </div>
                            <div className={styles.form}>
                                <div className={styles.formRow}>
                                    <div className={styles.formGroup}>
                                        <div className={styles.formLabel}>Địa chỉ IP</div>
                                        <input
                                            className={styles.formInput}
                                            value={form.ipAddress}
                                            onChange={(e) => onChange('ipAddress', e.target.value)}
                                            placeholder="192.168.1.100"
                                        />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <div className={styles.formLabel}>Cổng (Port)</div>
                                        <input
                                            className={styles.formInput}
                                            type="number"
                                            value={form.port}
                                            onChange={(e) => onChange('port', e.target.value)}
                                            placeholder="9100"
                                        />
                                    </div>
                                </div>
                                <div className={styles.formGroup}>
                                    <div className={styles.formLabel}>Timeout kết nối (ms)</div>
                                    <input
                                        className={styles.formInput}
                                        type="number"
                                        value={form.timeoutMs}
                                        onChange={(e) => onChange('timeoutMs', e.target.value)}
                                        placeholder="45000"
                                    />
                                    <div className={styles.formHint}>Thời gian chờ kết nối, từ 1000 đến 90000 ms</div>
                                </div>
                            </div>
                        </div>

                        {/* Print options */}
                        <div className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionTitle}>Tuỳ chọn in</div>
                            </div>
                            <div className={styles.form}>
                                <div className={styles.formGroup}>
                                    <div className={styles.formLabel}>Khổ giấy</div>
                                    <div className={styles.radioGroup}>
                                        {['58', '80'].map((w) => (
                                            <div
                                                key={w}
                                                className={`${styles.radioOption} ${form.paperWidth === w ? styles.radioOptionActive : ''}`}
                                                onClick={() => onChange('paperWidth', w)}
                                            >
                                                {w} mm
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className={styles.formGroup}>
                                    <div className={styles.formLabel}>Khoảng trắng cuối (1 – 5)</div>
                                    <div className={styles.sliderWrap}>
                                        <input
                                            type="range"
                                            min="1"
                                            max="5"
                                            step="1"
                                            value={form.trailingFeedLines}
                                            onChange={(e) => onChange('trailingFeedLines', e.target.value)}
                                            className={styles.slider}
                                        />
                                        <span className={styles.sliderValue}>{form.trailingFeedLines}</span>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    {[
                                        { key: 'cutAfterImagePrint', label: 'Cắt giấy sau khi in' },
                                        { key: 'isActive', label: 'Kích hoạt máy in này' },
                                    ].map(({ key, label }) => (
                                        <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <span style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A' }}>{label}</span>
                                            <label className={`${styles.toggle} ${form[key] ? styles.toggleOn : ''}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={!!form[key]}
                                                    onChange={() => onChange(key, !form[key])}
                                                />
                                                <span className={styles.toggleTrack} />
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Error */}
                        {error && (
                            <div style={{ color: '#EF4444', fontSize: 13, fontWeight: 600, padding: '8px 12px', background: '#FFF5F5', borderRadius: 8, border: '1px solid #FECACA' }}>
                                {error}
                            </div>
                        )}
                    </div>

                    {/* RIGHT: action panel */}
                    <div className={styles.printerRight}>
                        <div className={styles.printerActionCard}>
                            {/* Status */}
                            <div className={styles.printerStatus}>
                                <Printer size={20} color={form.isActive ? '#22C55E' : '#9CA3AF'} />
                                <div>
                                    <div className={styles.printerStatusLabel}>
                                        {form.isActive
                                            ? <span style={{ color: '#22C55E', fontWeight: 700 }}>Đang hoạt động</span>
                                            : <span style={{ color: '#9CA3AF', fontWeight: 700 }}>Tắt</span>
                                        }
                                    </div>
                                    {editingId && (
                                        <div className={styles.printerStatusMeta}>
                                            {form.paperWidth}mm · Port {form.port}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className={styles.printerDivider} />

                            {/* Buttons */}
                            <button
                                className={styles.btnSave}
                                style={{ width: '100%', justifyContent: 'center' }}
                                onClick={handleSave}
                                disabled={submitting}
                            >
                                <Save size={15} />
                                {submitting ? 'Đang lưu...' : 'Lưu kết nối'}
                            </button>

                            <button
                                className={styles.btnTest}
                                onClick={handleTestPrint}
                                disabled={testing || !editingId}
                            >
                                <Wifi size={15} />
                                {testing ? 'Đang in...' : 'In thử'}
                            </button>

                            {!editingId && (
                                <div className={styles.printerHint}>
                                    Lưu cấu hình trước khi in thử
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Toast */}
            {toast && (
                <div className={`${styles.toast} ${toast.type === 'success' ? styles.toastSuccess : styles.toastError}`}>
                    {toast.type === 'success' ? <CheckCircle size={15} /> : null}
                    {toast.text}
                </div>
            )}
        </div>
    );
}
